import * as THREE from "../../libs/three.js/build/three.module.js"
import { Annotation } from "../Annotation.js"
import { CameraMode } from "../defines.js"
import { View } from "./View.js"
import { Utils } from "../utils.js"
import { EventDispatcher } from "../EventDispatcher.js"

export class Scene extends EventDispatcher {
	constructor() {
		super()

		this.annotations = new Annotation()

		this.scene = new THREE.Scene()
		this.sceneBG = new THREE.Scene()
		this.scenePointCloud = new THREE.Scene()
		this.sceneGaussianSplat = new THREE.Scene()

		this.cameraP = new THREE.PerspectiveCamera(this.fov, 1, 0.1, 1000 * 1000)
		this.cameraO = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 1000 * 1000)
		this.cameraVR = new THREE.PerspectiveCamera()
		this.cameraBG = new THREE.Camera()
		this.cameraScreenSpace = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10)
		this.cameraMode = CameraMode.PERSPECTIVE
		this.overrideCamera = null
		this.pointclouds = []
		this.gaussianSplats = []

		this.measurements = []
		this.profiles = []
		this.volumes = []
		this.polygonClipVolumes = []
		this.cameraAnimations = []
		this.orientedImages = []
		this.images360 = []
		this.geopackages = []

		this.fpControls = null
		this.orbitControls = null
		this.earthControls = null
		this.geoControls = null
		this.deviceControls = null
		this.inputHandler = null

		this.view = new View()

		this.directionalLight = null

		this.initialize()
	}

	estimateHeightAt(position) {
		let height = null
		let fromSpacing = Infinity

		for (let pointcloud of this.pointclouds) {
			if (pointcloud.root.geometryNode === undefined) {
				continue
			}

			let pHeight = null
			let pFromSpacing = Infinity

			let lpos = position.clone().sub(pointcloud.position)
			lpos.z = 0
			let ray = new THREE.Ray(lpos, new THREE.Vector3(0, 0, 1))

			let stack = [pointcloud.root]
			while (stack.length > 0) {
				let node = stack.pop()
				let box = node.getBoundingBox()

				let inside = ray.intersectBox(box)

				if (!inside) {
					continue
				}

				let h = node.geometryNode.mean.z + pointcloud.position.z + node.geometryNode.boundingBox.min.z

				if (node.geometryNode.spacing <= pFromSpacing) {
					pHeight = h
					pFromSpacing = node.geometryNode.spacing
				}

				for (let index of Object.keys(node.children)) {
					let child = node.children[index]
					if (child.geometryNode) {
						stack.push(node.children[index])
					}
				}
			}

			if (height === null || pFromSpacing < fromSpacing) {
				height = pHeight
				fromSpacing = pFromSpacing
			}
		}

		return height
	}

	getBoundingBox(pointclouds = this.pointclouds) {
		let box = new THREE.Box3()

		this.scenePointCloud.updateMatrixWorld(true)
		this.referenceFrame.updateMatrixWorld(true)

		for (let pointcloud of pointclouds) {
			pointcloud.updateMatrixWorld(true)

			let pointcloudBox = pointcloud.pcoGeometry.tightBoundingBox ? pointcloud.pcoGeometry.tightBoundingBox : pointcloud.boundingBox
			let boxWorld = Utils.computeTransformedBoundingBox(pointcloudBox, pointcloud.matrixWorld)
			box.union(boxWorld)
		}

		return box
	}

	getGaussianSplatsBoundingBox(gaussianSplats = this.gaussianSplats) {
		let box = new THREE.Box3()

		this.sceneGaussianSplat.updateMatrixWorld(true)
		for (let gaussianSplat of gaussianSplats) {
			gaussianSplat.updateMatrixWorld(true)

			let pointcloudBox = gaussianSplat.getBoundingBox(true)
			if (pointcloudBox) {
				let boxWorld = Utils.computeTransformedBoundingBox(pointcloudBox, gaussianSplat.matrixWorld)
				box.union(boxWorld)
			}
		}
		return box
	}

	addPointCloud(pointcloud, resetSpeed = true) {
		this.pointclouds.push(pointcloud)
		this.scenePointCloud.add(pointcloud)

		this.dispatchEvent({
			type: "pointcloud_added",
			pointcloud: pointcloud,
			resetSpeed: resetSpeed,
		})
	}

	removePointCloudByName(name) {
		for (let i = this.scenePointCloud.children.length - 1; i >= 0; i--) {
			const child = this.scenePointCloud.children[i]
			if (child.name === name) {
				this.scenePointCloud.remove(child)
				this.disposePointcloudResources(child)

				// 清理 Potree._pointcloudTransformVersion 中该点云的缓存条目，
				// 否则 Map 以点云对象为 key 强引用，移除后该 entry 永不回收（泄漏点云对象及其 matrixWorld clone）。
				if (Potree._pointcloudTransformVersion) {
					Potree._pointcloudTransformVersion.delete(child)
				}
			}
		}

		this.pointclouds = this.pointclouds.filter(pc => pc.name !== name)
	}

	removeAllPointCloud() {
		this.pointclouds = []
		while (this.scenePointCloud.children.length > 0) {
			const child = this.scenePointCloud.children[0]
			this.scenePointCloud.remove(child)
			this.disposePointcloudResources(child)
		}

		// 清空全部点云的 transform 版本缓存，避免残留强引用。
		if (Potree._pointcloudTransformVersion) {
			Potree._pointcloudTransformVersion.clear()
		}
	}

	// 释放点云资源：geometry、material 及 material 内部的 texture。
	// PointCloudOctree 继承自 THREE.Object3D（不是 Points/Mesh），原 instanceof 守卫会跳过它，
	// 导致 material.dispose() 不被调用。且 THREE.Material.dispose 不会级联 dispose 内部 texture，
	// 需手动 dispose visibleNodesTexture/gradientTexture/matcapTexture/classificationTexture。
	disposePointcloudResources(pointcloud) {
		// 解除几何体引用
		if (pointcloud.geometry) {
			pointcloud.geometry.dispose()
		}

		// 释放材质（可能是单个或数组）
		const materials = Array.isArray(pointcloud.material) ? pointcloud.material : (pointcloud.material ? [pointcloud.material] : [])
		for (const mat of materials) {
			// 先释放 material 内部的 texture（three.js Material.dispose 不级联）
			const textureFields = ['visibleNodesTexture', 'gradientTexture', 'matcapTexture', 'classificationTexture']
			for (const field of textureFields) {
				if (mat[field] && mat[field].isTexture) {
					mat[field].dispose()
				}
			}
			// 再 dispose material 本身（触发 PotreeRenderer 的 shaders dispose 监听）
			mat.dispose()
		}
	}

	addGaussianSplats(gaussianSplat) {
		this.gaussianSplats.push(gaussianSplat)
		this.sceneGaussianSplat.add(gaussianSplat)

		this.dispatchEvent({
			type: "gaussianSplats_added",
			gaussianSplat: gaussianSplat,
		})
	}

	removeAllGaussianSplats() {
		this.gaussianSplats = []
		while (this.sceneGaussianSplat.children.length > 0) {
			const child = this.sceneGaussianSplat.children[0]
			// 先摘除再释放，避免 dispose 过程中再次遍历到自己
			this.sceneGaussianSplat.remove(child)

			// child.dispose() 释放对象自身持有的资源：
			//   SparkRenderer → render target / sort worker / ordering texture 等
			//   SplatMesh     → packed splats / ext splats
			//   SplatEdit     → edit buffers
			if (typeof child.dispose === "function") {
				child.dispose()
			}

			// geometry / material 用鸭子类型释放，**不能**用 instanceof THREE.Mesh 判断：
			// spark 的 SparkRenderer 继承的是它自带的那份 THREE(见 "Multiple instances
			// of Three.js" 警告)的 Mesh，在 Potree 侧 instanceof THREE.Mesh 恒为 false，
			// 会漏掉它构造期创建、且自身 dispose() 从不释放的 SplatGeometry —— 于是每次
			// 加载/销毁循环就泄漏 1 个 geometry。业务侧的干净释放请走
			// Potree.disposeSparkRenderer(viewer)(内部会调用本方法)。
			if (child.geometry && typeof child.geometry.dispose === "function") {
				child.geometry.dispose()
			}
			if (child.material) {
				const mats = Array.isArray(child.material) ? child.material : [child.material]
				for (const mat of mats) {
					if (mat && typeof mat.dispose === "function") mat.dispose()
				}
			}
		}
	}

	addVolume(volume) {
		this.volumes.push(volume)
		this.dispatchEvent({
			type: "volume_added",
			scene: this,
			volume: volume,
		})
	}

	addOrientedImages(images) {
		this.orientedImages.push(images)
		this.scene.add(images.node)

		this.dispatchEvent({
			type: "oriented_images_added",
			scene: this,
			images: images,
		})
	}

	removeOrientedImages(images) {
		let index = this.orientedImages.indexOf(images)
		if (index > -1) {
			this.orientedImages.splice(index, 1)

			this.dispatchEvent({
				type: "oriented_images_removed",
				scene: this,
				images: images,
			})
		}
	}

	add360Images(images) {
		this.images360.push(images)
		this.scene.add(images.node)

		this.dispatchEvent({
			type: "360_images_added",
			scene: this,
			images: images,
		})
	}

	remove360Images(images) {
		let index = this.images360.indexOf(images)
		if (index > -1) {
			this.images360.splice(index, 1)

			this.dispatchEvent({
				type: "360_images_removed",
				scene: this,
				images: images,
			})
		}
	}

	addGeopackage(geopackage) {
		this.geopackages.push(geopackage)
		this.scene.add(geopackage.node)

		this.dispatchEvent({
			type: "geopackage_added",
			scene: this,
			geopackage: geopackage,
		})
	}

	removeGeopackage(geopackage) {
		let index = this.geopackages.indexOf(geopackage)
		if (index > -1) {
			this.geopackages.splice(index, 1)

			this.dispatchEvent({
				type: "geopackage_removed",
				scene: this,
				geopackage: geopackage,
			})
		}
	}

	// 注意这是【销毁】语义，不是隐藏：末尾会 dispose 掉 volume，把 box/frame/label 置 null，
	// 之后该对象不可再用。想隐藏请设 volume.visible = false（详见 Volume 的 set visible 注释），
	// 不要把 addVolume/removeVolume 成对当显隐开关 —— 那样第二次显示时必然空指针。
	removeVolume(volume) {
		let index = this.volumes.indexOf(volume)
		if (index > -1) {
			this.volumes.splice(index, 1)

			this.dispatchEvent({
				type: "volume_removed",
				scene: this,
				volume: volume,
			})

			// 释放 volume 持有的资源（video/audio/pdf/3D模型/材质纹理/几何），
			// 在 volume_removed 事件之后调用，使监听者（VolumeTool.onRemove 等）仍能访问 volume 做自身清理。
			// 仅 YDBoxVolume 等实现了 dispose 的子类会触发资源释放。
			// 另注意：画面上消失是 VolumeTool.onRemove 里 scene.remove(volume) 的效果（它先执行），
			// 所以 dispose 内部那句 parent.remove(this) 走到时 parent 已是 null，纯属直接调
			// dispose 而不走本方法时的兜底。
			if (volume && typeof volume.dispose === 'function') {
				volume.dispose()
			}
		}
	}

	addCameraAnimation(animation) {
		this.cameraAnimations.push(animation)
		this.scene.add(animation.node)
		this.dispatchEvent({
			type: "camera_animation_added",
			scene: this,
			animation: animation,
		})
	}

	removeCameraAnimation(animation) {
		let index = this.cameraAnimations.indexOf(animation)
		console.log(this.cameraAnimations, animation, index, "8888888888888888")
		if (index > -1) {
			this.scene.remove(animation.node)
			this.cameraAnimations.splice(index, 1)
		}
	}

	removeAllCameraAnimation() {
		this.cameraAnimations.forEach(animation => {
			this.scene.remove(animation.node)
		})
		this.cameraAnimations = []
	}

	addPolygonClipVolume(volume) {
		this.polygonClipVolumes.push(volume)
		this.dispatchEvent({
			type: "polygon_clip_volume_added",
			scene: this,
			volume: volume,
		})
	}

	removePolygonClipVolume(volume) {
		let index = this.polygonClipVolumes.indexOf(volume)
		if (index > -1) {
			this.polygonClipVolumes.splice(index, 1)
			this.dispatchEvent({
				type: "polygon_clip_volume_removed",
				scene: this,
				volume: volume,
			})
		}
	}

	addMeasurement(measurement) {
		measurement.lengthUnit = this.lengthUnit
		measurement.lengthUnitDisplay = this.lengthUnitDisplay
		this.measurements.push(measurement)
		this.dispatchEvent({
			type: "measurement_added",
			scene: this,
			measurement: measurement,
		})
	}

	removeMeasurement(measurement) {
		let index = this.measurements.indexOf(measurement)
		if (index > -1) {
			this.measurements.splice(index, 1)
			this.dispatchEvent({
				type: "measurement_removed",
				scene: this,
				measurement: measurement,
			})
		}
	}

	addProfile(profile) {
		this.profiles.push(profile)
		this.dispatchEvent({
			type: "profile_added",
			scene: this,
			profile: profile,
		})
	}

	removeProfile(profile) {
		let index = this.profiles.indexOf(profile)
		if (index > -1) {
			this.profiles.splice(index, 1)
			this.dispatchEvent({
				type: "profile_removed",
				scene: this,
				profile: profile,
			})
		}
	}

	removeAllMeasurements() {
		while (this.measurements.length > 0) {
			this.removeMeasurement(this.measurements[0])
		}

		while (this.profiles.length > 0) {
			this.removeProfile(this.profiles[0])
		}

		while (this.volumes.length > 0) {
			this.removeVolume(this.volumes[0])
		}
	}

	removeAllClipVolumes() {
		let clipVolumes = this.volumes.filter(volume => volume.clip === true)
		for (let clipVolume of clipVolumes) {
			this.removeVolume(clipVolume)
		}

		while (this.polygonClipVolumes.length > 0) {
			this.removePolygonClipVolume(this.polygonClipVolumes[0])
		}
	}

	getActiveCamera() {
		if (this.overrideCamera) {
			return this.overrideCamera
		}

		if (this.cameraMode === CameraMode.PERSPECTIVE) {
			return this.cameraP
		} else if (this.cameraMode === CameraMode.ORTHOGRAPHIC) {
			return this.cameraO
		} else if (this.cameraMode === CameraMode.VR) {
			return this.cameraVR
		}

		return null
	}

	initialize() {
		this.referenceFrame = new THREE.Object3D()
		this.referenceFrame.matrixAutoUpdate = false
		this.scenePointCloud.add(this.referenceFrame)

		this.cameraP.up.set(0, 0, 1)
		this.cameraP.position.set(1000, 1000, 1000)
		this.cameraO.up.set(0, 0, 1)
		this.cameraO.position.set(1000, 1000, 1000)
		//this.camera.rotation.y = -Math.PI / 4;
		//this.camera.rotation.x = -Math.PI / 6;
		this.cameraScreenSpace.lookAt(new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, -1), new THREE.Vector3(0, 1, 0))

		this.directionalLight = new THREE.DirectionalLight(0xffffff, 0.5)
		this.directionalLight.position.set(10, 10, 10)
		this.directionalLight.lookAt(new THREE.Vector3(0, 0, 0))
		this.scenePointCloud.add(this.directionalLight)

		let light = new THREE.AmbientLight(0x555555) // soft white light
		this.scenePointCloud.add(light)

		{
			// background
			let texture = Utils.createBackgroundTexture(512, 512)

			texture.minFilter = texture.magFilter = THREE.NearestFilter
			texture.minFilter = texture.magFilter = THREE.LinearFilter
			// 重构178 废弃的 BufferGeometry 后缀几何体 替换为 PlaneGeometry
			// let bg = new THREE.Mesh(
			// 	new THREE.PlaneBufferGeometry(2, 2, 1),
			// 	new THREE.MeshBasicMaterial({
			// 		map: texture,
			// 	})
			// )
			let bg = new THREE.Mesh(
				new THREE.PlaneGeometry(2, 2, 1),
				new THREE.MeshBasicMaterial({
					map: texture,
				})
			)
			bg.material.depthTest = false
			bg.material.depthWrite = false
			this.sceneBG.add(bg)
		}

		// { // lights
		// 	{
		// 		let light = new THREE.DirectionalLight(0xffffff);
		// 		light.position.set(10, 10, 1);
		// 		light.target.position.set(0, 0, 0);
		// 		this.scene.add(light);
		// 	}

		// 	{
		// 		let light = new THREE.DirectionalLight(0xffffff);
		// 		light.position.set(-10, 10, 1);
		// 		light.target.position.set(0, 0, 0);
		// 		this.scene.add(light);
		// 	}

		// 	{
		// 		let light = new THREE.DirectionalLight(0xffffff);
		// 		light.position.set(0, -10, 20);
		// 		light.target.position.set(0, 0, 0);
		// 		this.scene.add(light);
		// 	}
		// }
	}

	addAnnotation(position, args = {}) {
		if (position instanceof Array) {
			args.position = new THREE.Vector3().fromArray(position)
		} else if (position.x != null) {
			args.position = position
		}
		let annotation = new Annotation(args)
		this.annotations.add(annotation)

		return annotation
	}

	getAnnotations() {
		return this.annotations
	}

	removeAnnotation(annotationToRemove) {
		this.annotations.remove(annotationToRemove)
	}
	// 通常不需要调用 特殊情况下调用
	destoryPdfWorker() {
		if (pdfjsLib.GlobalWorkerOptions.workerSrc) {
			pdfjsLib.GlobalWorkerOptions.workerSrc = null
		}
	}
}
